import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { FaFire } from 'react-icons/fa';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useConfigActions from '../../../hooks/APIHooks/appointments/useConfigActions';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import useWeekDaysActions from '../../../hooks/APIHooks/useWeekDaysActions';
import { createEditConfigSchema } from '../../../schemas';
import { SelectData, WeekDay } from '../../../types/common';
import { Config, ConfigReqBody } from '../../../types/configurations';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import { showTimeIn12HrsFormat } from '../../../utils/timeAndDate';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCheckboxField from '../Inputs/AcademiaCheckboxField';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTimePicker from '../Inputs/AcademiaTimePicker';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const UpdateAssociateConfigModal = ({
  config,
  handleModalClose,
}: {
  config?: Config;
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [weekDays, setWeekDays] = useState<Array<WeekDay>>([]);
  const [workingDays, setWorkingDays] = useState<Array<WeekDay>>([]);
  const [resetField, setResetField] = useState<boolean>(false);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { getServiceListHandler } = useServiceActions('serviceList');
  const { updateConfigHandler } = useConfigActions('');
  const { getWeekDaysHandler } = useWeekDaysActions();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // setting value of week days from database into state for create/update here
  useEffect(() => {
    if (getWeekDaysHandler.data?.data.length) {
      setWeekDays(getWeekDaysHandler.data.data);

      if (config) {
        const existingWorkingDays = getWeekDaysHandler.data.data.filter((day) =>
          config.working_days_with_names.some(
            (workingDay) => workingDay.value === day.value
          )
        );

        setWorkingDays(existingWorkingDays);

        setWeekDays((state) =>
          state.filter(
            (day) =>
              !existingWorkingDays.some(
                (workingDay) => workingDay.value === day.value
              )
          )
        );
      }
    }
  }, [getWeekDaysHandler.data, config]);

  const serviceOptions = useMemo(() => {
    if (!getServiceListHandler.data?.data) return [];

    return getServiceListHandler.data.data.map((service) => ({
      id: service.id,
      name: service.name,
    }));
  }, [getServiceListHandler.data]);

  const serviceOptionDescriptions = useMemo(() => {
    if (!getServiceListHandler.data?.data) return [];

    return getServiceListHandler.data.data.map((service) =>
      service.is_most_popular ? (
        <FaFire title='Popular Right Now' className='text-base-orange' />
      ) : (
        <></>
      )
    );
  }, [getServiceListHandler.data]);

  const updateAssociateConfigHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body: ConfigReqBody = {
        service: values.service.id,
        slot_duration: values.duration,
        lead_time: values.lead_time,
        finish_time: values.finish_time,
        appointment_buffer_time: values.buffer_time,
        allow_rescheduling: values.allow_rescheduling,
        reschedule_limit: values.reschedule_limit || 0,
        working_days: workingDays.map((day) => day.value),
      };

      await updateConfigHandler
        .mutateAsync({ configId: config?.id as number, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Configuration Updated!!');

            handleModalClose();
            queryClient.invalidateQueries({
              queryKey: ['service-configs-by-id', currentUser?.id],
            });
          } else {
            toast.error(res.message || 'Configuration Update Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // function to add working day in array declared here
  const addWorkingDay = (value: SelectData) => {
    setWorkingDays((state) => [...state, { value: value.id, day: value.name }]);

    setWeekDays((state) => state.filter((day) => day.value !== value.id));
  };

  // function to remove working day from array declared here
  const removeWorkingDay = (workingDay: WeekDay) => {
    setWorkingDays((state) =>
      state.filter((day) => day.value !== workingDay.value)
    );

    setWeekDays((state) =>
      [...state, workingDay].sort((dayA, dayB) => dayA.value - dayB.value)
    );
  };

  // rendering update associate config modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='update configuration' />

      <Formik
        initialValues={{
          associate: config
            ? { id: config.associate, name: config.associate_full_name }
            : SELECT_FIELD_DEFAULT_VALUE,
          duration: config?.slot_duration || '',
          service: config
            ? { id: config.service_data.id, name: config.service_data.name }
            : SELECT_FIELD_DEFAULT_VALUE,
          minute_conversion: config
            ? {
                id: config.minute_conversion_data.id,
                name: config.minute_conversion_data.name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          lead_time: config?.lead_time || '',
          finish_time: config?.finish_time || '',
          buffer_time: config?.appointment_buffer_time || '',
          week_day: SELECT_FIELD_DEFAULT_VALUE,
          allow_rescheduling: config?.allow_rescheduling || false,
          reschedule_limit: config?.reschedule_limit || '',
        }}
        validationSchema={createEditConfigSchema}
        onSubmit={updateAssociateConfigHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaFormSelect
              name='service'
              label='Select Service'
              options={serviceOptions}
              optionDescription={serviceOptionDescriptions}
              defaultValue={props.values.service}
              showSearchInput
              isRequired
            />

            {workingDays.length ? (
              <div className='border-2 border-base-cyan px-2 py-3 rounded-lg flex flex-wrap gap-2'>
                {workingDays.map((workingDay) => (
                  <SkillBadge
                    key={workingDay.value}
                    badgeLabel={workingDay.day}
                    color='#388229'
                    isEditing
                    onClickRemove={() => removeWorkingDay(workingDay)}
                  />
                ))}
              </div>
            ) : null}

            <AcademiaFormSelect
              name='week_day'
              label='Select Working Days'
              options={weekDays.map((day) => ({
                id: day.value,
                name: day.day,
              }))}
              setValueOnChange={addWorkingDay}
              resetSelectField={resetField}
              setResetSelectField={() => setResetField(true)}
              defaultValue={SELECT_FIELD_DEFAULT_VALUE}
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='duration'
              label='Slot Duration (Min.)'
              placeholder='Enter configuration slot duration here...'
              min={10}
              isRequired
            />

            <div className='grid md:grid-cols-2 gap-4'>
              <AcademiaTimePicker
                name='lead_time'
                label='Starting Time'
                placeholder='Starting time here...'
                defaultTime={
                  config?.lead_time
                    ? showTimeIn12HrsFormat(config.lead_time as string)
                    : undefined
                }
                isRequired
              />

              <AcademiaTimePicker
                name='finish_time'
                label='Finishing Time'
                placeholder='Finishing time here...'
                defaultTime={
                  config?.finish_time
                    ? showTimeIn12HrsFormat(config.finish_time as string)
                    : undefined
                }
                isRequired
              />
            </div>

            {/* <AcademiaFormSelect
              name='minute_conversion'
              label='Select Service Rate'
              options={
                getMinuteConversionListHandler.data?.data.data.length
                  ? getMinuteConversionListHandler.data.data.data.map(
                      (conversion) => ({
                        id: conversion.id,
                        name: conversion.name,
                      })
                    )
                  : []
              }
              defaultValue={props.values.minute_conversion}
              showSearchInput
              isRequired
            /> */}

            <AcademiaInputField
              type='number'
              name='buffer_time'
              label='Buffer Time (Min.)'
              placeholder='Enter configuration buffer time here...'
              min={2}
              isRequired
            />

            <AcademiaCheckboxField
              name='allow_rescheduling'
              label='Allow rescheduling?'
            />

            <AnimatePresence
              initial={false}
              mode='wait'
              onExitComplete={() => null}
            >
              {props.values.allow_rescheduling ? (
                <motion.div
                  initial={{ y: -50, opacity: 0, height: 0 }}
                  animate={{ y: 0, opacity: 1, height: 'fit-content' }}
                  exit={{ y: -50, opacity: 0, height: 0 }}
                >
                  <AcademiaInputField
                    type='number'
                    name='reschedule_limit'
                    label='Appointment Reschedule Limit'
                    placeholder='Enter reschedule limit here...'
                    max={5}
                    isRequired
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>

            <div className='!mt-6'>
              <AcademiaButton
                title={
                  props.values.associate.id === -1 ||
                  props.values.service.id === -1 ||
                  props.values.minute_conversion.id === -1 ||
                  !workingDays.length
                    ? 'Please, select an associate, a service, a service rate & some working days first!'
                    : ''
                }
                type='submit'
                variant='success'
                icon={<RxUpdate className='size-6' />}
                isDisabled={
                  props.values.associate.id === -1 ||
                  props.values.service.id === -1 ||
                  props.values.minute_conversion.id === -1 ||
                  !workingDays.length
                }
                isLoading={props.isSubmitting}
                fullWidth
              >
                Save Configuration
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default UpdateAssociateConfigModal;
